import { useMemo, useState } from "react";
import styled from "styled-components";
import { Link, useParams } from "react-router-dom";

import { findProduct } from "../data/products";
import { useLight } from "../context/LightContext";
import LightSwitch from "../components/LightSwitch";

export default function Product() {
  const { id } = useParams();
  const product = findProduct(id);
  const { isLit, toggleOne } = useLight();

  const [qty, setQty] = useState(1);
  const [active, setActive] = useState(0);
  const [order, setOrder] = useState(null);

  /* Thumbnail strip, in fixed order.
     Slots 0 and 1 are the switch-driven pair; anything after is static. */
  const shots = useMemo(() => {
    if (!product) return [];
    return [
      { src: product.off, kind: "off", caption: "Fikur" },
      { src: product.on, kind: "on", caption: "Ndezur" },
      product.room && { src: product.room, kind: "static", caption: "Ambient" },
      product.extra && { src: product.extra, kind: "static", caption: "Detaj" },
    ].filter(Boolean);
  }, [product]);

  if (!product) {
    return (
      <Missing>
        <p>Ky produkt nuk ekziston.</p>
        <Back to="/">← Kthehu te koleksioni</Back>
      </Missing>
    );
  }

  const lit = isLit(product.id);
  const total = product.price * qty;

  /* The switch owns slots 0 and 1. If the shopper is looking at a static
     shot (ambient / detail) we leave it alone — flipping the switch
     re-themes the page but does not yank them off the photo they chose. */
  const onPair = active <= 1;
  const index = onPair ? (lit ? 1 : 0) : active;

  const pickShot = (i) => {
    setActive(i);
    if (i <= 1) {
      const wantLit = i === 1;
      if (wantLit !== lit) toggleOne(product.id);
    }
  };

  return (
    <Wrap>
      <Crumbs>
        <Back to="/">← Koleksioni</Back>
        <Sep>/</Sep>
        <span>{product.name}</span>
      </Crumbs>

      <Layout>
        {/* ---------------- gallery ---------------- */}
        <Gallery>
          <Main $lit={onPair && lit}>
            {shots.map((s, i) => (
              <Shot
                key={i}
                src={s.src}
                alt={i === index ? product.name : ""}
                aria-hidden={i !== index}
                $show={i === index}
              />
            ))}

            {onPair && (
              <OnFrame>
                <LightSwitch
                  lit={lit}
                  onToggle={() => {
                    toggleOne(product.id);
                    setActive(lit ? 0 : 1);
                  }}
                />
              </OnFrame>
            )}
          </Main>

          {shots.length > 1 && (
            <Thumbs>
              {shots.map((s, i) => (
                <Thumb
                  key={i}
                  type="button"
                  $active={i === index}
                  onClick={() => pickShot(i)}
                  aria-label={s.caption}
                >
                  <img src={s.src} alt="" />
                  <Cap>{s.caption}</Cap>
                </Thumb>
              ))}
            </Thumbs>
          )}
        </Gallery>

        {/* ---------------- details ---------------- */}
        <Info>
          <Type>{product.type}</Type>
          <Name>{product.name}</Name>

          <PriceRow>
            <Price>€{product.price}</Price>
            {product.oldPrice && <Old>€{product.oldPrice}</Old>}
            <Stock $low={product.stock <= 4}>
              {product.stock > 0 ? `${product.stock} në stok` : "Jashtë stokut"}
            </Stock>
          </PriceRow>

          {product.description && <Desc>{product.description}</Desc>}

          <Tip>
            <strong>Provoje.</strong> Prek çelësin te fotoja, ose zgjidh
            “Ndezur” poshtë, për ta parë këtë llambë në një dhomë të errët.
          </Tip>

          {product.details && (
            <Table>
              <tbody>
                {product.details.map(([k, v]) => (
                  <tr key={k}>
                    <th>{k}</th>
                    <td>{v}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <QtyRow>
            <Label>Sasia</Label>
            <Stepper>
              <StepBtn
                type="button"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                aria-label="Zvogëlo"
              >
                −
              </StepBtn>
              <Count>{qty}</Count>
              <StepBtn
                type="button"
                onClick={() =>
                  setQty((q) => Math.min(product.stock || 99, q + 1))
                }
                aria-label="Rrit"
              >
                +
              </StepBtn>
            </Stepper>
            <Total>€{total}</Total>
          </QtyRow>

          <Buttons>
            <Primary type="button" onClick={() => setOrder("cash")}>
              Paguaj në dorëzim
            </Primary>
            <Secondary type="button" onClick={() => setOrder("card")}>
              Kartelë krediti / debiti
            </Secondary>
          </Buttons>

          <Fine>
            Transporti falas mbi €150 · Dorëzimi 2–4 ditë pune · Kthim brenda 14
            ditësh
          </Fine>
        </Info>
      </Layout>

      {order && (
        <Sheet role="dialog" aria-modal="true">
          <SheetBox>
            <SheetTitle>
              {order === "cash" ? "Porosi e regjistruar" : "Vazhdo me pagesën"}
            </SheetTitle>
            <SheetText>
              {order === "cash"
                ? `${qty} × ${product.name} — €${total}. Paguan kur ta pranosh porosinë.`
                : `${qty} × ${product.name} — €${total}. Këtu do të hapej faqja e pagesës.`}
            </SheetText>
            <SheetNote>Kjo është një faqe demo — asgjë nuk u dërgua.</SheetNote>
            <Primary type="button" onClick={() => setOrder(null)}>
              Në rregull
            </Primary>
          </SheetBox>
        </Sheet>
      )}
    </Wrap>
  );
}

/* ------------------------------------------------------------------ */

const Wrap = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: clamp(24px, 4vh, 44px) clamp(20px, 5vw, 64px)
    clamp(60px, 10vh, 120px);
`;

const Missing = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 120px clamp(20px, 5vw, 64px);
  display: flex;
  flex-direction: column;
  gap: 18px;
  color: var(--ink-soft);
`;

const Crumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin-bottom: clamp(18px, 4vh, 46px);
  flex-wrap: wrap;
`;

const Sep = styled.span`
  opacity: 0.5;
`;

const Back = styled(Link)`
  color: inherit;
  text-decoration: none;

  &:hover {
    color: var(--ink);
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  gap: clamp(30px, 5vw, 78px);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Gallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: sticky;
  top: 96px;

  @media (max-width: 900px) {
    position: static;
  }
`;

const Main = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  border-radius: 2px;
  background: var(--bg-soft);
  box-shadow: ${({ $lit }) => ($lit ? "var(--glow)" : "none")};
  transition:
    box-shadow var(--t-theme) var(--ease),
    background var(--t-theme) var(--ease);
`;

const Shot = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 750ms var(--ease);
`;

const OnFrame = styled.div`
  position: absolute;
  left: 20px;
  bottom: 20px;
  z-index: 3;

  @media (max-width: 560px) {
    left: 14px;
    bottom: 14px;
  }
`;

const Thumbs = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;

  @media (max-width: 560px) {
    gap: 7px;
  }
`;

const Thumb = styled.button`
  position: relative;
  padding: 0;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  border-radius: 2px;
  background: var(--bg-soft);
  opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  outline: 1px solid
    ${({ $active }) => ($active ? "var(--accent)" : "transparent")};
  outline-offset: 2px;
  transition:
    opacity 260ms var(--ease),
    outline-color 260ms var(--ease);

  &:hover {
    opacity: 1;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Cap = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 5px 6px;
  font-size: 8px;
  letter-spacing: 0.24em;
  text-align: center;
  text-transform: uppercase;
  color: #f4efe6;
  background: linear-gradient(
    to top,
    rgba(12, 11, 10, 0.72),
    rgba(12, 11, 10, 0)
  );
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 4px;
`;

const Type = styled.span`
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-soft);
`;

const Name = styled.h1`
  font-family: "Instrument Serif", Georgia, serif;
  font-weight: 400;
  font-size: clamp(32px, 8vw, 68px);
  line-height: 1;
  letter-spacing: -0.03em;
  margin-top: -8px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 14px;
`;

const Price = styled.span`
  font-size: 24px;
`;

const Old = styled.span`
  font-size: 15px;
  color: var(--ink-soft);
  text-decoration: line-through;
`;

const Stock = styled.span`
  margin-left: auto;
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ $low }) => ($low ? "var(--accent)" : "var(--ink-soft)")};
`;

const Desc = styled.p`
  font-size: 15px;
  line-height: 1.7;
  color: var(--ink-soft);
`;

const Tip = styled.p`
  font-size: 13px;
  line-height: 1.6;
  color: var(--ink-soft);
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 2px;

  strong {
    color: var(--ink);
    font-weight: 500;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: 12px 0;
    border-bottom: 1px solid var(--line);
    font-weight: 400;
  }

  th {
    color: var(--ink-soft);
    width: 42%;
  }

  @media (max-width: 420px) {
    th,
    td {
      padding: 10px 0;
    }
    th {
      width: 46%;
    }
  }
`;

const QtyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding-top: 4px;
  flex-wrap: wrap;

  @media (max-width: 420px) {
    gap: 12px;
  }
`;

const Label = styled.span`
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-soft);
`;

const Stepper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid var(--line);
  border-radius: 999px;
`;

const StepBtn = styled.button`
  width: 46px;
  height: 46px;
  font-size: 17px;
  color: var(--ink-soft);

  &:hover {
    color: var(--ink);
  }
`;

const Count = styled.span`
  min-width: 26px;
  text-align: center;
  font-size: 15px;
`;

const Total = styled.span`
  margin-left: auto;
  font-size: 19px;
`;

const Buttons = styled.div`
  display: grid;
  gap: 10px;
`;

const Primary = styled.button`
  width: 100%;
  padding: 17px 22px;
  border-radius: 999px;
  background: var(--ink);
  color: var(--bg);
  font-size: 13px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  transition:
    background var(--t-theme) var(--ease),
    color var(--t-theme) var(--ease),
    opacity 200ms var(--ease);

  &:hover {
    opacity: 0.86;
  }
`;

const Secondary = styled.button`
  width: 100%;
  padding: 17px 22px;
  border-radius: 999px;
  border: 1px solid var(--ink);
  color: var(--ink);
  font-size: 13px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  transition:
    border-color var(--t-theme) var(--ease),
    color var(--t-theme) var(--ease),
    background 200ms var(--ease);

  &:hover {
    background: color-mix(in srgb, var(--ink) 8%, transparent);
  }
`;

const Fine = styled.p`
  font-size: 12px;
  line-height: 1.7;
  color: var(--ink-soft);
`;

const Sheet = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  @media (max-width: 560px) {
    align-items: flex-end;
    padding: 0;
  }
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
`;

const SheetBox = styled.div`
  width: min(420px, 100%);
  padding: 32px;
  border-radius: 4px;

  @media (max-width: 560px) {
    padding: 26px 22px calc(26px + env(safe-area-inset-bottom));
    border-radius: 14px 14px 0 0;
  }
  background: var(--bg);
  border: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SheetTitle = styled.h2`
  font-family: "Instrument Serif", Georgia, serif;
  font-weight: 400;
  font-size: 28px;
  letter-spacing: -0.02em;
`;

const SheetText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-soft);
`;

const SheetNote = styled.p`
  font-size: 11px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
  margin-bottom: 6px;
`;
